import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { getDashboardRoute } from "../../auth/getDashboardRoute";
import { HOME_PATH } from "../../constants/routes";
import { useActiveSection } from "../../hooks/useActiveSection";
import { confirmAbandonReportGeneration } from "../../utils/reportGenerationGuard";
import { Icon } from "./icons";
import Logo from "./Logo";
import { scrollToSection } from "./scroll";

const SECTION_IDS = ["features", "faq"];
const NO_SECTION_IDS: string[] = [];

/** Sign-in button, or the account menu once the visitor is signed in (same behaviour as AuthNavMenu). */
function AccountControl({ onNavigate }: { onNavigate?: () => void }) {
  const basename = process.env.PUBLIC_URL || "";
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (isLoading) return null;

  if (!isAuthenticated || !user) {
    return (
      <Link to="/signin" className="rl-btn rl-btn--primary" onClick={onNavigate}>
        Sign in
      </Link>
    );
  }

  const name = user.userName?.trim() || user.email?.split("@")[0] || "Account";
  const dashboardPath = getDashboardRoute(user, basename);

  const go = async () => {
    const ok = await confirmAbandonReportGeneration("navigate away");
    setOpen(false);
    if (!ok) return;
    onNavigate?.();
    navigate(dashboardPath);
  };

  const signOut = async () => {
    const ok = await confirmAbandonReportGeneration("log out");
    setOpen(false);
    if (!ok) return;
    onNavigate?.();
    try {
      await logout();
    } finally {
      navigate("/signin", { replace: true });
    }
  };

  return (
    <div className="rl-account" ref={ref}>
      <button
        type="button"
        className="rl-btn rl-btn--primary"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {name} <span aria-hidden="true">&nbsp;▾</span>
      </button>
      {open && (
        <div className="rl-account__menu" role="menu">
          <button type="button" role="menuitem" onClick={go}>
            Dashboard
          </button>
          <button type="button" role="menuitem" onClick={signOut}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

interface LandingNavProps {
  /** "lite" swaps the section links for the ReImagine Lite / ReImagine 2.0 pair. */
  variant?: "landing" | "lite";
}

export default function LandingNav({ variant = "landing" }: LandingNavProps) {
  const isLite = variant === "lite";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const active = useActiveSection(isLite ? NO_SECTION_IDS : SECTION_IDS);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <header className={`rl-nav${scrolled ? " is-scrolled" : ""}`}>
      <div className="rl-nav__in">
        <Link to={HOME_PATH} className="rl-brand" aria-label="Go to home page" title="Go to home page">
          <Logo />
          <i className="rl-brand__sep" aria-hidden="true" />
          <span className="rl-brand__name">
            <span>Re</span>
            <b>Imagine</b>
            <i>2.0</i>
          </span>
        </Link>

        <nav className="rl-links" aria-label="ReImagine 2.0">
          {isLite ? (
            <>
              <Link to="/reimagine-lite" className="is-active" aria-current="page">
                ReImagine Lite
              </Link>
              <Link to={HOME_PATH}>ReImagine 2.0</Link>
            </>
          ) : (
            <>
              <Link to="/reimagine-lite">ReImagine Lite</Link>
              <a href="#features" className={active === "features" ? "is-active" : ""} onClick={jump("features")}>
                Features
              </a>
              <a href="#faq" className={active === "faq" ? "is-active" : ""} onClick={jump("faq")}>
                FAQ
              </a>
            </>
          )}
        </nav>

        <div className="rl-nav__end">
          <AccountControl onNavigate={() => setMenuOpen(false)} />
          <button
            type="button"
            className="rl-menu-btn"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name={menuOpen ? "close" : "menu"} size={22} />
          </button>
        </div>
      </div>

      <nav className={`rl-mobile${menuOpen ? " is-open" : ""}`} aria-label="ReImagine 2.0 menu">
        <Link to="/reimagine-lite" onClick={() => setMenuOpen(false)}>
          ReImagine Lite
        </Link>
        {isLite ? (
          <Link to={HOME_PATH} onClick={() => setMenuOpen(false)}>
            ReImagine 2.0
          </Link>
        ) : (
          <>
            <a href="#features" onClick={jump("features")}>
              Features
            </a>
            <a href="#faq" onClick={jump("faq")}>
              FAQ
            </a>
          </>
        )}
      </nav>
    </header>
  );
}
